/**
 * ASCII Art Generator - 彩虹效果计算
 *
 * 流动彩虹，基于位置的颜色变化
 */

import type { RainbowConfig } from '../animation/types'

/**
 * HSL 转 RGB
 */
function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs((h / 60) % 2 - 1))
  const m = l - c / 2

  let r = 0, g = 0, b = 0

  if (h < 60) { r = c; g = x; b = 0 }
  else if (h < 120) { r = x; g = c; b = 0 }
  else if (h < 180) { r = 0; g = c; b = x }
  else if (h < 240) { r = 0; g = x; b = c }
  else if (h < 300) { r = x; g = 0; b = c }
  else { r = c; g = 0; b = x }

  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255)
  }
}

/**
 * 计算彩虹效果
 *
 * @param charX 字符X位置
 * @param charY 字符Y位置
 * @param time 当前时间
 * @param config 彩虹配置
 * @returns RGB颜色
 */
export function calculateRainbowEffect(
  charX: number,
  charY: number,
  time: number,
  config: RainbowConfig
): { r: number; g: number; b: number } {
  const { speed, hueRange, saturation, lightness } = config

  // 基于位置和时间计算色相
  const positionOffset = (charX + charY) * 0.5
  const timeOffset = time * speed * 60 // 转换为度数
  const hue = (positionOffset + timeOffset) % hueRange

  return hslToRgb(hue, saturation, lightness)
}

/**
 * 彩虹效果类
 */
export class RainbowEffect {
  private config: RainbowConfig
  private time = 0

  constructor(config: Partial<RainbowConfig> = {}) {
    this.config = {
      speed: 1,
      hueRange: 360,
      saturation: 0.8,
      lightness: 0.5,
      ...config
    }
  }

  update(deltaTime: number): void {
    this.time += deltaTime
  }

  calculate(charX: number, charY: number): { r: number; g: number; b: number } {
    return calculateRainbowEffect(charX, charY, this.time, this.config)
  }

  setConfig(config: Partial<RainbowConfig>): void {
    this.config = { ...this.config, ...config }
  }

  getConfig(): RainbowConfig {
    return { ...this.config }
  }

  reset(): void {
    this.time = 0
  }
}

/**
 * 从设置创建彩虹配置
 */
export function createRainbowConfigFromSettings(
  speed: number = 1,
  saturation: number = 0.8
): RainbowConfig {
  return {
    speed,
    hueRange: 360,
    saturation,
    lightness: 0.5
  }
}